/**
 * Cart recovery for Klaviyo abandoned-cart flows.
 *
 * Tracking: on every cart addition (Horizon CartAddEvent) sends the
 * "Added to Cart Extended" event to Klaviyo with the full cart snapshot,
 * including line item properties (custom dozen flavors, delivery fields),
 * plus two CTA-ready links:
 *  - CartUrl        - plain cart link (phase 1, same-browser cookie cart)
 *  - RestoreCartUrl - cart link with a "restore-cart" payload (phase 2,
 *    cross-device restore); falls back to CartUrl when the URL is too long.
 *
 * Restore: when the page is opened with ?restore-cart=<payload>, shows a
 * full-screen "restoring your cart" overlay, rebuilds the cart from the
 * payload (clear + re-add with properties) and reloads the cart page.
 * Dormant unless the parameter is present.
 */
import { ThemeEvents, CartAddEvent } from '@theme/events';

const EVENT_NAME = 'Added to Cart Extended';
const RESTORE_PARAM = 'restore-cart';
const RESTORE_URL_MAX_LENGTH = 4000;
const TRACK_DEBOUNCE_MS = 500;

/**
 * @typedef {Object} CartItem
 * @property {number} product_id
 * @property {number} variant_id
 * @property {string} sku
 * @property {string} product_title
 * @property {string} variant_title
 * @property {number} quantity
 * @property {number} price
 * @property {number} final_line_price
 * @property {string} image
 * @property {string} url
 * @property {Record<string, string>} [properties]
 */

/**
 * @typedef {Object} Cart
 * @property {CartItem[]} items
 * @property {number} total_price
 * @property {number} item_count
 */

/**
 * Queues an entry for klaviyo.js. Never assigns window.klaviyo (that would
 * block the official snippet); before the library loads, entries go to the
 * official _klOnsite queue that klaviyo.js drains on init.
 * @param {unknown[]} entry
 */
const pushToKlaviyo = (entry) => {
  const scope = /** @type {any} */ (window);

  if (typeof scope.klaviyo?.push === 'function') {
    scope.klaviyo.push(entry);
    return;
  }

  scope._klOnsite = scope._klOnsite || [];
  scope._klOnsite.push(entry);
};

/**
 * @returns {{ cart: string, cartAdd: string, cartClear: string }}
 */
const getRoutes = () => {
  const themeRoutes = typeof Theme !== 'undefined' ? Theme.routes : null;
  const cart = themeRoutes?.cart_url || '/cart';
  return {
    cart,
    cartAdd: themeRoutes?.cart_add_url || '/cart/add.js',
    cartClear: cart.replace(/\/cart$/, '/cart/clear.js'),
  };
};

/**
 * @param {unknown} value
 * @returns {string}
 */
const encodeBase64Url = (value) => {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * @param {string} encoded
 * @returns {unknown}
 */
const decodeBase64Url = (encoded) => {
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

/**
 * @returns {Promise<Cart>}
 */
const fetchCart = async () => {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });

  if (!response.ok) {
    throw new Error(`Cart request failed: ${response.status}`);
  }

  return response.json();
};

/**
 * @param {Cart} cart - Cart object from /cart.js
 * @param {string} absoluteCartUrl
 * @returns {string}
 */
const buildRestoreCartUrl = (cart, absoluteCartUrl) => {
  const lines = cart.items.map((item) => {
    /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */
    const line = { id: item.variant_id, quantity: item.quantity };

    if (item.properties && Object.keys(item.properties).length) {
      line.properties = item.properties;
    }

    return line;
  });

  const url = `${absoluteCartUrl}?${RESTORE_PARAM}=${encodeBase64Url(lines)}`;
  return url.length <= RESTORE_URL_MAX_LENGTH ? url : absoluteCartUrl;
};

/**
 * Sends the cart snapshot to Klaviyo. The flow-trigger event is tracked only
 * for additions; profile properties are refreshed on every cart change
 * (including removals and an emptied cart) so emails always render the
 * latest cart at send time.
 * @param {boolean} shouldTrack - Whether the change included a cart addition
 */
const syncCartToKlaviyo = async (shouldTrack) => {
  try {
    const cart = await fetchCart();
    const absoluteCartUrl = `${window.location.origin}${getRoutes().cart}`;
    const items = cart.items.map((item) => ({
      ProductID: item.product_id,
      VariantID: item.variant_id,
      SKU: item.sku,
      ProductName: item.product_title,
      VariantName: item.variant_title,
      Quantity: item.quantity,
      ItemPrice: item.price / 100,
      RowTotal: item.final_line_price / 100,
      ImageURL: item.image,
      URL: `${window.location.origin}${item.url}`,
      Properties: item.properties || {},
    }));

    const restoreCartUrl = cart.items.length ? buildRestoreCartUrl(cart, absoluteCartUrl) : absoluteCartUrl;

    if (shouldTrack && cart.items.length) {
      pushToKlaviyo([
        'track',
        EVENT_NAME,
        {
          $value: cart.total_price / 100,
          ItemCount: cart.item_count,
          ItemNames: items.map((item) => item.ProductName),
          Items: items,
          CartUrl: absoluteCartUrl,
          RestoreCartUrl: restoreCartUrl,
        },
      ]);
    }

    pushToKlaviyo([
      'identify',
      {
        LastCartValue: cart.total_price / 100,
        LastCartItemCount: cart.item_count,
        LastCartItems: items,
        LastCartUrl: absoluteCartUrl,
        LastCartRestoreUrl: restoreCartUrl,
        LastCartUpdatedAt: new Date().toISOString(),
      },
    ]);
  } catch (error) {
    console.error('[cart-recovery] Failed to sync cart snapshot:', error);
  }
};

const showRestoreOverlay = () => {
  const overlay = document.createElement('div');
  overlay.className = 'cart-recovery';
  overlay.setAttribute('role', 'status');
  overlay.innerHTML = `
    <style>
      .cart-recovery {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        padding-inline: 24px;
        background: var(--color-background, #fff);
        color: var(--color-foreground, #1f1f1f);
        font-family: var(--font-body--family, sans-serif);
        text-align: center;
      }
      .cart-recovery__spinner {
        width: 44px;
        height: 44px;
        border: 3px solid currentColor;
        border-block-start-color: transparent;
        border-radius: 50%;
        animation: cart-recovery-spin 0.8s linear infinite;
      }
      .cart-recovery__message {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 600;
      }
      .cart-recovery__note {
        margin: 0;
        font-size: 0.875rem;
        opacity: 0.7;
      }
      @keyframes cart-recovery-spin {
        to { transform: rotate(360deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .cart-recovery__spinner { animation-duration: 2s; }
      }
    </style>
    <span class="cart-recovery__spinner" aria-hidden="true"></span>
    <p class="cart-recovery__message">Restoring your cart&hellip;</p>
    <p class="cart-recovery__note">Please wait, this will only take a moment.</p>
  `;

  if (document.body) {
    document.body.appendChild(overlay);
  } else {
    document.addEventListener('DOMContentLoaded', () => document.body.appendChild(overlay));
  }
};

/**
 * @param {string} encoded - Base64url payload from the restore-cart URL parameter
 */
const restoreCart = async (encoded) => {
  showRestoreOverlay();
  const routes = getRoutes();
  let lines = null;

  try {
    lines = decodeBase64Url(encoded);
  } catch {
    console.warn('[cart-recovery] Invalid restore payload, showing current cart.');
  }

  if (!Array.isArray(lines) || !lines.length) {
    window.location.replace(routes.cart);
    return;
  }

  try {
    await fetch(routes.cartClear, { method: 'POST', headers: { Accept: 'application/json' } });
  } catch (error) {
    console.error('[cart-recovery] Failed to clear cart before restore:', error);
  }

  for (const line of lines) {
    if (!line?.id || !line?.quantity) continue;

    try {
      const response = await fetch(routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: line.id, quantity: line.quantity, properties: line.properties || {} }],
        }),
      });

      if (!response.ok) {
        console.warn(`[cart-recovery] Line skipped (variant ${line.id}): ${response.status}`);
      }
    } catch (error) {
      console.warn(`[cart-recovery] Line skipped (variant ${line.id}):`, error);
    }
  }

  window.location.replace(routes.cart);
};

const init = () => {
  const encoded = new URL(window.location.href).searchParams.get(RESTORE_PARAM);

  if (encoded) {
    restoreCart(encoded);
    return;
  }

  let syncTimer = 0;
  let pendingAdd = false;
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (/** @type {any} */ (event).detail?.data?.didError) return;

    pendingAdd = pendingAdd || event instanceof CartAddEvent;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      const shouldTrack = pendingAdd;
      pendingAdd = false;
      syncCartToKlaviyo(shouldTrack);
    }, TRACK_DEBOUNCE_MS);
  });
};

init();
